import { getPolygonLabelPoint } from "../../PolygonsLayer/getPolygonLabelPoint";
import { measureLabelWidth } from "../../PolygonsLayer/measureLabelWidth";
import {
  LABEL_ANCHOR_PRECISION_PT,
  LAYOUT_LABEL_FONT_FAMILY,
  LAYOUT_LABEL_PADDING_X_FACTOR,
  LAYOUT_LABEL_PADDING_Y_FACTOR,
} from "../constants";
import type {
  FloorPlanLayoutPoint,
  LayoutBounds,
  LayoutLabelCandidate,
} from "../types";
import type { BuildLabelCandidates } from "./types";

// Text width grows linearly with font size, so it is measured once at this size and scaled.
const MEASURE_FONT_SIZE = 100;
const BADGE_HEIGHT_PER_FONT_SIZE = 1 + LAYOUT_LABEL_PADDING_Y_FACTOR * 2;

const boundsOf = (rings: FloorPlanLayoutPoint[][]): LayoutBounds => {
  const bounds = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };
  for (const ring of rings) {
    for (const point of ring) {
      if (point.x < bounds.minX) bounds.minX = point.x;
      if (point.y < bounds.minY) bounds.minY = point.y;
      if (point.x > bounds.maxX) bounds.maxX = point.x;
      if (point.y > bounds.maxY) bounds.maxY = point.y;
    }
  }
  return bounds;
};

// One candidate per labelled shape whose label has somewhere to sit.
export const buildLabelCandidates: BuildLabelCandidates = ({
  shapes,
  fontWeight,
}) => {
  const candidates: LayoutLabelCandidate[] = [];

  for (const shape of shapes) {
    if (!shape.label || shape.label.text === "") continue;
    const anchor = getPolygonLabelPoint({
      points: shape.label.points,
      precision: LABEL_ANCHOR_PRECISION_PT,
    });
    if (anchor === null) continue;

    const textWidthPerFontSize =
      measureLabelWidth({
        text: shape.label.text,
        fontSize: MEASURE_FONT_SIZE,
        fontFamily: LAYOUT_LABEL_FONT_FAMILY,
        fontWeight,
      }) / MEASURE_FONT_SIZE;

    candidates.push({
      shapeId: shape.id,
      text: shape.label.text,
      rgb: shape.rgb,
      anchor,
      bounds: boundsOf(shape.rings),
      badgeWidthPerFontSize:
        textWidthPerFontSize + LAYOUT_LABEL_PADDING_X_FACTOR * 2,
      badgeHeightPerFontSize: BADGE_HEIGHT_PER_FONT_SIZE,
    });
  }

  return candidates;
};
